// pages/TicketDetailPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeft, Send, Paperclip, X } from "lucide-react";
import { BASE_URL, CDN_BASE_URL } from "../config";

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const roleLabel = (r) => {
  if (r === "admin") return "Support";
  if (r === "org_admin") return "Org Support";
  return "You";
};

export default function TicketDetailPage() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");
  const { id } = useParams();
  const navigate = useNavigate();

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const token = user?.token;

  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const fileInputRef = useRef(null);

  const API_BASE = BASE_URL;

  // ---------------- FETCH TICKET ----------------
  const fetchTicket = async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ticket/${id}`, {
        headers: { "x-access-token": token },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "NOT_FOUND");
      setTicket(json?.data || json);
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  // ---------------- FILE HANDLING ----------------
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (attachments.length + files.length > MAX_FILES) {
      toast.error(t("support.max_files", "حداکثر ۵ فایل مجاز است"));
      return;
    }

    for (const f of files) {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(
          t("support.file_too_large", "حجم هر فایل باید کمتر از ۵ مگابایت باشد")
        );
        return;
      }
    }

    setAttachments((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ---------------- SEND MESSAGE ----------------
  const sendMessage = async () => {
    if (!token) return;

    if (!text.trim() && attachments.length === 0) {
      toast.error(
        t("support.text_or_file_required", "متن یا فایل ضمیمه الزامی است")
      );
      return;
    }

    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      attachments.forEach((f) => fd.append("attachments", f));

      const res = await fetch(`${API_BASE}/ticket/${id}/message`, {
        method: "POST",
        headers: { "x-access-token": token },
        body: fd,
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "FAILED");

      setTicket(json?.data || json);
      setText("");
      setAttachments([]);
      toast.success(t("support.message_sent", "پیام ارسال شد"));
    } catch (e) {
      toast.error(e?.message || t("common.unknown_error"));
    } finally {
      setSending(false);
    }
  };

  // ---------------- UI ----------------
  const mainContentClass = `flex-1 flex flex-col px-2 pb-5 lg:px-0 lg:pb-0 ${
    isRtl ? "md:mr-64" : "md:ml-64"
  }`;
  const mainSectionClass = `flex-1 overflow-y-auto p-4 md:p-6 bg-gray-100 rounded-[16px] ${
    isRtl ? "lg:me-3 lg:mb-3" : "lg:ms-3 lg:mb-3"
  }`;

  return (
    <div className="flex h-screen">
      <Helmet>
        <title>{t("support.ticket_detail", "جزئیات تیکت")}</title>
      </Helmet>

      <Sidebar />
      <div className={mainContentClass}>
        <Header />
        <main className={mainSectionClass}>
          {/* Back */}
          <button
            onClick={() => navigate(-1)}
            className="mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white"
          >
            <ArrowLeft size={16} />
            {t("common.back", "بازگشت")}
          </button>

          {loading ? (
            <div className="bg-white p-6 rounded-xl">
              {t("common.loading", "در حال بارگذاری...")}
            </div>
          ) : !ticket ? (
            <div className="bg-white p-6 rounded-xl text-red-600">
              {t("support.not_found", "تیکت پیدا نشد")}
            </div>
          ) : (
            <div className="bg-white rounded-xl border overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b bg-gray-50">
                <div className="font-bold text-lg">{ticket.subject}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {new Date(ticket.createdAt).toLocaleString()}
                </div>
              </div>

              {/* Messages */}
              <div className="p-4 space-y-3 max-h-[55vh] overflow-y-auto">
                {(ticket.messages || []).map((m, idx) => {
                     console.log(m?.sender)
                  const mine =
                    String(m?.sender?._id || m?.sender) === String(user?._id);
                  return (
                    <div
                      key={idx}
                      className={`flex ${
                        mine ? "justify-start" : "justify-end"
                      }`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl border p-3 text-sm  ${
                          mine ? "bg-green-100" : "bg-gray-100"
                        } `}
                      >
                        <div className="text-xs text-gray-500 mb-1">
                          {mine
                            ? t("support.you", "شما")
                            : m?.sender?.name}
                        </div>
                        {m.text && <div>{m.text}</div>}
                        {Array.isArray(m.attachments) &&
                          m.attachments.map((a, i) => (
                            <a
                              key={i}
                              href={CDN_BASE_URL + a?.url}
                              target="_blank"
                              rel="noreferrer"
                              className="block text-xs text-blue-600 underline mt-1"
                            >
                              {a?.filename} {t("support.attachment")} #{i + 1}
                            </a>
                          ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Composer */}
              <div className="p-4 border-t">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t("support.write_message")}
                  className="w-full border rounded-lg px-3 py-2 text-sm mb-2"
                />

                {/* Attachments preview */}
                {attachments.length > 0 && (
                  <div className="mb-2 space-y-1">
                    {attachments.map((f, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between text-xs bg-gray-100 px-2 py-1 rounded"
                      >
                        <span className="truncate">
                          {f.name} ({Math.round(f.size / 1024)} KB)
                        </span>
                        <button onClick={() => removeAttachment(i)}>
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileSelect}
                  />

                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm"
                  >
                    <Paperclip size={16} />
                    {t("support.attach", "ضمیمه")}
                  </button>

                  <button
                    onClick={sendMessage}
                    disabled={sending}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white"
                  >
                    <Send size={16} />
                    {t("common.send", "ارسال")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
